/// <amd-module name="Wit/WorkItemDataManager/Sources/WorkItemDataProviderSource" />
import { equals, format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { deserializeVssJsonObject } from "VSS/Platform/Util/Serialization";
import { CacheStatus } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { WorkItemMetadataCacheInformationManager } from "Wit/WorkItemDataManager/CacheManagers/WorkItemMetadataCacheInformationManager";
import {
    FieldRuleType,
    IFieldProjectData,
    ILinkTypes,
    IWorkItemData,
    IWorkItemDataProviderPayload,
    IWorkItemDataSource,
    IWorkItemMetadataCacheResult,
    IWorkItemTypeData
} from "Wit/WorkItemDataManager/Contracts";
import * as Resources from "Wit/WorkItemDataManager/Resources";
import { canDeserializeRules } from "Wit/WorkItemDataManager/Utils";

export const WorkItemDataProviderId = "ms.vss-work-web.work-item-data-provider";

interface IWorkItemDataProviderRequestProperties {
    id: number;
    "include-history"?: boolean;
    "include-in-recent-activity"?: boolean;
    "is-deleted"?: boolean;
    cookie?: string;

    /** Whether meta data for the work item should be included or not */
    includeMetadata?: boolean;
}

export class WorkItemDataProviderSource implements IWorkItemDataSource {
    private _dataProviderData: IWorkItemDataProviderPayload;
    private _cacheInformationManager: WorkItemMetadataCacheInformationManager;
    private _pageContext: IVssPageContext;

    constructor(pageContext: IVssPageContext, cacheInformationManager: WorkItemMetadataCacheInformationManager) {
        this._pageContext = pageContext;
        this._cacheInformationManager = cacheInformationManager;
    }

    private getWorkItemData(id: number): IWorkItemData | null {
        if (!this._containsWorkItemData(id)) {
            return null;
        }

        const providerData: IWorkItemDataProviderPayload = this._getDataProviderData();
        return this._processWorkItemData(id, providerData);
    }

    public beginGetWorkItemData(
        ids: number[],
        isDeleted?: boolean,
        includeInRecentActivity?: boolean,
        includeHistory?: boolean
    ): Promise<IWorkItemData[] | null> {
        if (ids.length !== 1) {
            // This data provider can only resolve a single work item
            return Promise.resolve(null);
        }

        const id = ids[0];

        // Check local data island first
        if (!includeHistory && this._containsWorkItemData(id)) {
            return Promise.resolve([this.getWorkItemData(id)!]);
        }

        // Fall back, query data provider
        const dataProviderRequestProperties: IWorkItemDataProviderRequestProperties = {
            id: ids[0],
            "include-history": includeHistory,
            "include-in-recent-activity": includeInRecentActivity,
            "is-deleted": isDeleted,
            cookie: this._cacheInformationManager && this._cacheInformationManager.readInformationForNonWITLocation()
        };

        return this._pageContext
            .getService<IVssContributionService>("IVssContributionService")
            .getDataAsync(WorkItemDataProviderId, dataProviderRequestProperties, true)
            .then((dataProviderData: IWorkItemDataProviderPayload) => {
                if (!dataProviderData) {
                    return Promise.resolve(null);
                }
                this._dataProviderData = dataProviderData;
                return [this._processWorkItemData(id, this._dataProviderData)];
            });
    }

    private _processWorkItemData(id: number, workItemProviderData: IWorkItemDataProviderPayload, clear: boolean = true): IWorkItemData {
        let workItemData = workItemProviderData["work-item-data"];

        if (!workItemData) {
            const serializedWorkItemData = workItemProviderData["work-item-data-serialized"];
            if (serializedWorkItemData) {
                workItemData = deserializeVssJsonObject<IWorkItemData>(serializedWorkItemData) ?? undefined;
            }
        }

        if (!workItemData) {
            throw new Error(format(Resources.WorkItemNotFoundClientException, id));
        }

        if (clear) {
            delete workItemProviderData["work-item-id"];
            delete workItemProviderData["work-item-data"];
            delete workItemProviderData["work-item-data-serialized"];
        }

        return workItemData;
    }

    public beginGetWorkItemTypeData(projectId: string, typeNames: string[]): Promise<IWorkItemTypeData[] | null> {
        if (typeNames.length > 1) {
            // This data provider can only ever return data for a single work item type
            return Promise.resolve(null);
        }

        const pageData: IWorkItemDataProviderPayload = this._getDataProviderData();
        const cachedData = pageData && pageData["work-item-type-data"];
        if (cachedData) {
            if (isCacheUpToDate(cachedData)) {
                // When we get here, the server thinks we have the data, but the client still tries to resolve
                // it from the data provider (i.e., it's not in the cache), so return null to allow falling back to another
                // data source
                return Promise.resolve(null);
            }

            if (cachedData.data) {
                const workItemTypeData: IWorkItemTypeData = { ...cachedData.data, rules: { ...cachedData.data.rules, fieldRules: {} } };

                if (!equals(workItemTypeData.projectGuid, projectId, true)) {
                    return Promise.resolve(null);
                }

                if (!equals(typeNames[0], workItemTypeData.referenceName, true) && !equals(typeNames[0], workItemTypeData.name, true)) {
                    return Promise.resolve(null);
                }

                if (canDeserializeRules(cachedData.data)) {
                    const fieldRules = deserializeVssJsonObject<{ [fieldId: number]: FieldRuleType[] }>(cachedData.data.rules.fieldRules as string);
                    if (fieldRules) {
                        workItemTypeData.rules.fieldRules = fieldRules;
                    }
                } else {
                    workItemTypeData.rules.fieldRules = <{ [fieldId: number]: FieldRuleType[] }>(<any>workItemTypeData.rules.fieldRules);
                }

                return Promise.resolve([workItemTypeData]);
            }
        }

        return Promise.resolve(null);
    }

    public beginGetFields(): Promise<IFieldEntry[] | null> {
        return Promise.resolve(null);
    }

    public beginGetFieldProjectData(projectId: string): Promise<IFieldProjectData | null> {
        const pageData: IWorkItemDataProviderPayload = this._getDataProviderData();
        const cachedData = pageData && pageData["work-item-project-field-data"];
        if (cachedData) {
            if (isCacheUpToDate(cachedData)) {
                return Promise.resolve(null);
            }

            if (cachedData.data && cachedData.data.projects) {
                const isProjectIdOrNameMatch = cachedData.data.projects.some(project => {
                    return equals(projectId, project.guid, true) || equals(projectId, project.name, true);
                });

                if (!isProjectIdOrNameMatch) {
                    return Promise.resolve(null);
                }
            }

            return Promise.resolve(cachedData.data);
        }

        return Promise.resolve(null);
    }

    public beginGetLinkTypes(): Promise<ILinkTypes | null> {
        const pageData: IWorkItemDataProviderPayload = this._getDataProviderData();
        const cachedData = pageData && pageData["work-item-link-data"];
        if (cachedData) {
            if (isCacheUpToDate(cachedData)) {
                return Promise.resolve(null);
            }

            return Promise.resolve(cachedData.data);
        }
        return Promise.resolve(null);
    }

    public beginGetConstantSets(ids: string[]): Promise<{ [key: string]: string[] } | null> {
        const pageData: IWorkItemDataProviderPayload = this._getDataProviderData();
        const cachedData = pageData && pageData["work-item-constant-sets"];
        if (cachedData) {
            if (isCacheUpToDate(cachedData)) {
                return Promise.resolve({});
            }

            let subsetOfInterest: { [key: string]: string[] } = {};
            ids.forEach(i => {
                subsetOfInterest[i] = cachedData.data[i];
            });

            return Promise.resolve(subsetOfInterest);
        }
        return Promise.resolve(null);
    }

    private _containsWorkItemData(id: number): boolean {
        const pageData: IWorkItemDataProviderPayload = this._getDataProviderData();
        return pageData && pageData["work-item-id"] === id && pageData["work-item-data"] !== undefined;
    }

    private _getDataProviderData(): IWorkItemDataProviderPayload {
        if (!this._dataProviderData) {
            const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
            const dataProviderData = contributionService.getData<IWorkItemDataProviderPayload>(WorkItemDataProviderId);
            if (dataProviderData) {
                this._dataProviderData = dataProviderData;
            }
        }
        return this._dataProviderData;
    }
}

function isCacheUpToDate<T>(cachedData: IWorkItemMetadataCacheResult<T>): boolean {
    return (
        cachedData.status === CacheStatus.UpToDate ||
        (typeof cachedData.status === "string" && cachedData.status === CacheStatus[CacheStatus.UpToDate])
    );
}
